"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { COUPA_SETTINGS_ROUTE, differingItemCount, useCanReadCoupaReport } from "@/components/inventory/coupa-report/report";
import { analyticsDashboardRoute, carryAnalyticsParams } from "@/config/analytics-nav";
import { type NavSection, type SiteMapItem, siteMapFor } from "@/config/sitemap";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useAnalyticsCustomDashboards } from "@/hooks/use-analytics-custom-dashboards";
import { taskScopeParams, useEffectiveTaskScope } from "@/hooks/use-effective-task-scope";
import { useWebSocketSubscription } from "@/hooks/use-websocket-subscription";
import { filterItemsByAccess } from "@/lib/menu-access";
import { itemModulesEnabled } from "@/lib/module-gate";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { tenantMatchesAllowedSubDomains } from "@/lib/tenant-flags";
import { UserType } from "@/types/enums";
import { AlertType } from "@/types/enums/alert-type";
import { emailQueryOptions } from "@/api/provider/comms/email/email-query";
import { faxesQueryOptions } from "@/api/provider/fax/faxes-query";
import { labQueryOptions } from "@/api/provider/lab/lab-query";
import { useSetupChecklist } from "@/api/provider/setup-checklist/setup-checklist-query";
import { taskQueryKeys, useTaskCounts } from "@/api/provider/tasks/tasks-query";
import { chatQueryOptions } from "@/query/comms/chat";
import { socialQueryOptions } from "@/query/comms/social";
import { coupaQueryOptions } from "@/query/integrations/coupa";
import { useChatFiltersStore } from "@/stores/chat-store";
const TASK_ALERT_TYPES = [AlertType.TASK_ASSIGNED, AlertType.TASK_COMPLETED, AlertType.TASK_UPDATED];
interface UseMenuItemsOptions {
  /** The sub-nav section the route falls in, or undefined for the main nav. */
  navSection: NavSection | undefined;
  currentRoute: string | undefined;
}

/**
 * Hook to centralize all menu item filtering logic.
 * Handles module filtering, role/permission filtering, and alert counts.
 */
export function useMenuItems({
  navSection,
  currentRoute
}: UseMenuItemsOptions) {
  const {
    currentUser,
    currentLocationId,
    visibleLocationIds
  } = useUser();
  const {
    tenant
  } = useTenant();
  // Tasks are always-on (no module gate); still scoped to users with tasks:read.
  const tasksEnabled = hasPermission(currentUser, PERMISSIONS.TASKS_READ);
  const callsEnabled = !!tenant?.enabledModules?.includes("CALLING") && hasPermission(currentUser, PERMISSIONS.MESSAGING_CALLS_READ);
  // Match the contract-owned Calls location default so the badge agrees with
  // the list: current location first, else a scoped user's visible locations.
  // Full-access users with no current location stay unscoped (show all).
  // Total tasks stay unscoped (API only applies locationIds to linked.call).
  const isLocationScopedUser = !hasPermission(currentUser, PERMISSIONS.LOCATIONS_SEE_ALL) && visibleLocationIds.length > 0;
  const callLocationIds = useMemo(() => {
    if (currentLocationId) return [currentLocationId];
    if (isLocationScopedUser) return visibleLocationIds;
    return undefined;
  }, [currentLocationId, isLocationScopedUser, visibleLocationIds]);
  // The badge counts the same tasks the panel lists, so switching scope moves
  // both. Counting the default scope while the panel showed another one is
  // what left the number unchanged when the filter changed.
  const taskScope = useEffectiveTaskScope();
  const {
    data: taskCounts
  } = useTaskCounts(tasksEnabled, {
    locationIds: callLocationIds,
    ...taskScopeParams(taskScope, currentUser?.id)
  });
  const taskCount = taskCounts?.total ?? 0;
  const callLinkedTaskCount = callsEnabled ? taskCounts?.linked?.call ?? 0 : 0;

  // Invalidate task count when task-related WebSocket events arrive
  const queryClient = useQueryClient();
  const {
    latestMessage: latestTaskMessage
  } = useWebSocketSubscription(TASK_ALERT_TYPES, tasksEnabled, "TaskCountBadge");
  useEffect(() => {
    if (latestTaskMessage) {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.all
      });
    }
  }, [latestTaskMessage, queryClient]);
  const locationId = useChatFiltersStore(state => state.chatFilters.locationId);
  const chatEnabled = !!tenant?.enabledModules?.includes("TEXTING") && hasPermission(currentUser, PERMISSIONS.MESSAGING_CHATS_READ);
  const {
    data: unreadChatsCount = 0
  } = useQuery({
    ...chatQueryOptions.unreadCount(locationId),
    enabled: chatEnabled,
    select: data => data ?? 0
  });
  const socialEnabled = !!tenant?.enabledModules?.includes("SOCIALS") && hasPermission(currentUser, PERMISSIONS.MESSAGING_SOCIAL_READ);
  const {
    data: unreadSocialCount = 0
  } = useQuery({
    ...socialQueryOptions.unreadCount(),
    enabled: socialEnabled
  });
  const emailEnabled = !!tenant?.enabledModules?.includes("EMAILS") && hasPermission(currentUser, PERMISSIONS.MESSAGING_EMAILS_READ);
  const {
    data: unreadEmailCount = 0
  } = useQuery({
    ...emailQueryOptions.clinicFolderCounts(),
    enabled: emailEnabled,
    select: data_0 => data_0?.inboxUnread ?? 0
  });
  const faxEnabled = !!tenant?.enabledModules?.includes("FAX");
  const {
    data: unreadFaxCount = 0
  } = useQuery({
    ...faxesQueryOptions.unreadCount(),
    enabled: faxEnabled,
    select: data_1 => data_1 ?? 0
  });

  // Same gate as the Labs sitemap entry: OR across vendor modules, plus labs:read.
  const labsEnabled = !!tenant?.enabledModules?.some(m => ["LABS", "FULLSCRIPT_LABS"].includes(m)) && hasPermission(currentUser, PERMISSIONS.LABS_READ);
  const {
    data: labResultsReadyCount = 0
  } = useQuery({
    ...labQueryOptions.resultsReadyCount(),
    enabled: labsEnabled
  });
  const coupaReceivingEnabled = !!tenant?.enabledModules?.includes("COUPA") && hasPermission(currentUser, PERMISSIONS.INVENTORY_RECEIVE);
  const {
    data: coupaOrdersToReceive = 0
  } = useQuery({
    ...coupaQueryOptions.toReceive(callLocationIds),
    enabled: coupaReceivingEnabled,
    select: data_2 => data_2?.count ?? 0
  });
  const coupaReportEnabled = useCanReadCoupaReport();
  const {
    data: coupaDifferences = 0
  } = useQuery({
    ...coupaQueryOptions.reconciliation(),
    enabled: coupaReportEnabled,
    select: data_3 => differingItemCount(data_3?.rows ?? [])
  });
  const {
    data: setupChecklist
  } = useSetupChecklist();
  const setupGuideOpen = !!setupChecklist?.items.length && !setupChecklist.finishedAt;

  // Saved dashboards are per-tenant, so the analytics sidebar picks them up at
  // runtime rather than from the static config the other sections use.
  const {
    dashboards: customDashboards
  } = useAnalyticsCustomDashboards(navSection?.customDashboards === true);

  // Every analytics row carries the filters already in force, so moving between
  // two reports keeps the comparison the user is in the middle of making.
  const searchParams = useSearchParams();
  const carriedSearch = navSection?.carriesFilters ? searchParams.toString() : "";
  const deactivatedPages = currentUser?.type === UserType.PROVIDER ? tenant?.providerDeactivatedPages : tenant?.patientDeactivatedPages;
  const menuItems = useMemo(() => {
    if (navSection) {
      // The setup guide only earns a row while it is still open. No other
      // section has such a row, so the filter is a no-op for them.
      const sidebar = setupGuideOpen ? navSection.sidebar : navSection.sidebar.filter(item => item.route !== "/settings/setup-guide");
      const sectionItems = filterItemsByAccess(sidebar, currentUser, currentRoute, tenant?.enabledModules, tenant?.subDomain);
      const carried = new URLSearchParams(carriedSearch);
      // The href gains the carried filters, but active state still compares
      // against the plain route, so keep that as an alias to match on.
      const withCarriedFilters = (items: SiteMapItem[]): SiteMapItem[] => items.map(item_0 => ({
        ...item_0,
        route: item_0.route ? carryAnalyticsParams(item_0.route, carried) : item_0.route,
        routeAliases: item_0.route ? [item_0.route, ...(item_0.routeAliases ?? [])] : item_0.routeAliases,
        children: item_0.children && withCarriedFilters(item_0.children)
      }));
      const withCoupaDifferences = (list: SiteMapItem[]): SiteMapItem[] => coupaReportEnabled ? list.map(item_1 => ({
        ...item_1,
        alertCount: item_1.route === COUPA_SETTINGS_ROUTE ? coupaDifferences : item_1.alertCount,
        children: item_1.children && withCoupaDifferences(item_1.children)
      })) : list;
      const items_0 = withCoupaDifferences(navSection.carriesFilters ? withCarriedFilters(sectionItems) : sectionItems);
      if (!navSection.customDashboards || customDashboards.length === 0) {
        return items_0;
      }
      return [...items_0, {
        name: "Custom",
        children: customDashboards.map(dashboard => ({
          name: dashboard.name,
          route: carryAnalyticsParams(analyticsDashboardRoute(dashboard.id), carried),
          routeAliases: [analyticsDashboardRoute(dashboard.id)],
          description: dashboard.description
        }))
      }];
    }

    // Filter by module and deactivated pages
    const baseFiltered = siteMapFor(tenant).filter(item_2 => {
      if (tenant?.enabledModules && !itemModulesEnabled(item_2, tenant.enabledModules)) {
        return false;
      }
      return !deactivatedPages?.includes(item_2.route ?? "");
    });

    // Filter children by module, tenant visibility, and apply role/permission filtering
    const withFilteredChildren = baseFiltered.map(item_3 => {
      const filteredChildren = item_3.children?.filter(child => {
        if (tenant?.enabledModules && !itemModulesEnabled(child, tenant.enabledModules)) {
          return false;
        }
        if (!tenantMatchesAllowedSubDomains({
          tenantSubDomain: tenant?.subDomain,
          allowedSubDomains: child.tenantSubDomains,
          includeAllInDevelopment: false
        })) {
          return false;
        }
        return true;
      });
      return {
        ...item_3,
        children: filteredChildren
      };
    });

    // Add alert counts
    const withAlertCounts = withFilteredChildren.map(item_4 => {
      if (item_4.name === "Tasks") {
        return {
          ...item_4,
          alertCount: taskCount
        };
      }
      if (item_4.name === "Clinical" && item_4.children) {
        return {
          ...item_4,
          children: item_4.children.map(child_0 => child_0.name === "Labs" ? {
            ...child_0,
            alertCount: labResultsReadyCount
          } : child_0)
        };
      }
      if (item_4.name === "Inventory" && item_4.children && coupaReceivingEnabled) {
        return {
          ...item_4,
          children: item_4.children.map(child_1 => child_1.name === "Purchase Orders" ? {
            ...child_1,
            alertCount: coupaOrdersToReceive
          } : child_1)
        };
      }
      if (item_4.name === "Communications" && item_4.children) {
        return {
          ...item_4,
          children: item_4.children.map(child_2 => {
            if (child_2.name === "Chat") {
              return {
                ...child_2,
                alertCount: unreadChatsCount
              };
            }
            if (child_2.name === "Email") {
              return {
                ...child_2,
                alertCount: unreadEmailCount
              };
            }
            if (child_2.name === "Social") {
              return {
                ...child_2,
                alertCount: unreadSocialCount
              };
            }
            if (child_2.name === "Fax") {
              return {
                ...child_2,
                alertCount: unreadFaxCount
              };
            }
            if (child_2.name === "Calls") {
              // Land on the same location-filtered list the badge counts.
              // Keep `/comms/call` as a route alias so the nav stays active when
              // query params are stripped by route matching.
              const callsRoute = callLocationIds?.length ? `/comms/call?locationIds=${callLocationIds.join(",")}` : child_2.route;
              return {
                ...child_2,
                route: callsRoute,
                routeAliases: [...(child_2.routeAliases ?? []), ...(callsRoute !== child_2.route && child_2.route ? [child_2.route] : [])],
                alertCount: callLinkedTaskCount
              };
            }
            return child_2;
          })
        };
      }
      return item_4;
    });

    // Apply role/permission filtering to items and their children
    return filterItemsByAccess(withAlertCounts, currentUser, currentRoute, tenant?.enabledModules);
  }, [navSection, carriedSearch, customDashboards, currentUser, tenant?.kind, tenant?.enabledModules, deactivatedPages, taskCount, callLinkedTaskCount, callLocationIds, unreadChatsCount, unreadEmailCount, unreadSocialCount, unreadFaxCount, labResultsReadyCount, coupaReceivingEnabled, coupaOrdersToReceive, coupaReportEnabled, coupaDifferences, currentRoute, setupGuideOpen]);
  return menuItems;
}